import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Pill, SkeletonText } from "@astrum/ui";
import { api } from "../lib/api.js";
import { RAG_LABEL, RAG_TONE, UpdateBody } from "../components/ProjectStatusTab.js";
import type { StatusUpdate } from "../components/ProjectStatusTab.js";
import "../components/project-status-tab.css";
import "./share-status.css";

/**
 * The public, read-only status page behind a share link.
 *
 * No session and no app shell: whoever holds the link reads the latest update
 * and the headline numbers, nothing more. Told not to be indexed both here and
 * by the API's X-Robots-Tag, since a link in a forwarded email can end up
 * anywhere.
 */

type Shared = {
  project: { code: string; name: string; ownerName: string; startDate: string | null; targetDate: string | null };
  progress: number;
  counts: { open: number; overdue: number; done: number };
  update: StatusUpdate | null;
};

function day(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
}

export function ShareStatus() {
  const { token = "" } = useParams();
  const [data, setData] = useState<Shared | undefined>(undefined);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const previousTitle = document.title;
    document.title = "Project status";
    return () => {
      meta.remove();
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    api
      .get<Shared>(`/share/status/${encodeURIComponent(token)}`)
      .then(setData)
      // Any failure reads the same to a stranger: the link does not work.
      .catch(() => setInvalid(true));
  }, [token]);

  return (
    <main className="share-status">
      <div className="share-status__card">
        {invalid ? (
          <>
            <h1>This link is not valid</h1>
            <p>It may have expired or been revoked. Ask the project owner for a new one.</p>
          </>
        ) : !data ? (
          <SkeletonText lines={6} />
        ) : (
          <>
            <p className="share-status__code">{data.project.code}</p>
            <h1>{data.project.name}</h1>
            <dl className="share-status__meta">
              <div>
                <dt>Owner</dt>
                <dd>{data.project.ownerName}</dd>
              </div>
              <div>
                <dt>Target</dt>
                <dd>{day(data.project.targetDate)}</dd>
              </div>
              <div>
                <dt>Complete</dt>
                <dd>{data.progress}%</dd>
              </div>
              <div>
                <dt>Open / overdue</dt>
                <dd>
                  {data.counts.open} / {data.counts.overdue}
                </dd>
              </div>
            </dl>
            {data.update ? (
              <article className={`status-card status-card--${data.update.health}`}>
                <header>
                  <Pill tone={RAG_TONE[data.update.health]} dot>
                    {RAG_LABEL[data.update.health]}
                  </Pill>
                  <span>
                    Week of {day(`${data.update.period}T12:00:00`)} · {data.update.authorName}
                  </span>
                </header>
                <UpdateBody update={data.update} />
              </article>
            ) : (
              <p>No status update has been posted yet.</p>
            )}
          </>
        )}
      </div>
    </main>
  );
}
